import { Modal } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { createFieldSelect, createFieldTextArea } from "../../../../components/input/template-builder/template-builder.logic";
import { createColumnDate, createColumnInput, createColumnSelect } from "../../../../components/table/c-table.logic";
import i18n from "../../../../i18n";
import router from "../../../../router";
import store from "../../../../store";
import { conditionFormChecker, copyForm, putField } from "../../../../utils/form";
import { callRequestDoc, callRequestStatus, callRequestType } from "../../storage-location/request/request.section";
import { callUser, checkUserCompanyOperation } from "../../storage-location/request/requestor/requestor.section";
import { defineAsyncComponent } from "vue";

const CIcon = defineAsyncComponent(() => import("../../../../components/icon/CIcon"));

export const callManufactureSampleAttachment = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "MANUFACTURE_SAMPLE_ATTACHMENT", value: searchQuery });

export const callLicenseManufactureSampleTerms = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LICENSE_MANUFACTURE_SAMPLE_TERMS", value: searchQuery });

export const callUserObjective = ({ searchQuery }) =>
    store
        .dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "USER_OBJECTIVE", value: searchQuery })
        .then(items => items.filter(item => ["4"].includes(item.key)));

export const callManufactureSample = ({ searchQuery, userInfo, isOfficerCreate }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "expireDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") }
    ];
    if (!isOfficerCreate) {
        where.push(
            { openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 1
            { name: "refUserCompany.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id },

            { logic: "OR", openBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 2
            { name: "refUserCompany.userAuthorizes.isActive", type: "Boolean", operator: "=", value: true },
            { name: "refUserCompany.userAuthorizes.isTemp", type: "Boolean", operator: "=", value: false },
            { name: "refUserCompany.userAuthorizes.refStatus.key", type: "String", operator: "=", value: "active" },
            { name: "refUserCompany.userAuthorizes.refAuthorizeType.key", type: "String", operator: "=", value: "2" },
            { name: "refUserCompany.userAuthorizes.refAuthorize.id", type: "UUID", operator: "=", value: userInfo?.refParent?.id || userInfo?.id },

            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.isActive", type: "Boolean", operator: "=", value: true },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.isTemp", type: "Boolean", operator: "=", value: false },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refStatus.key", type: "String", operator: "=", value: "active" },
            { name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refAuthorizeType.key", type: "String", operator: "=", value: "1" },
            {
                name: "refUserCompany.userAuthorizes.refAuthorize.userAuthorizes.refAuthorize.id",
                type: "Getpath",
                operator: "=",
                value: "refUserCompany.id"
            },
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" }, // 2
            { closeBucket: true, constant: true, name: "1", type: "String", operator: "=", value: "1" } // 1
        );
    }
    if (searchQuery) {
        where.push({ name: "approveCode", type: "String", operator: "like", value: searchQuery });
    }

    return store
        .dispatch("resource/v1/transaction/manufactureSample/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "approveCode", "refUserCompany.id", "refUserCompany.fullNameTh"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "approveCode", type: "DESC" }]
            }
        })
        .then(page => page.data);
};

export const callManufactureSampleFull = ({ id }) => {
    return store
        .dispatch("resource/v1/transaction/manufactureSample/search", {
            criteria: {
                select: ["**", "refUserCompany.refPersonType.*", "refUser.refPersonType.*"],
                page: 0,
                size: 1,
                where: [{ name: "id", type: "UUID", operator: "=", value: id }],
                children: [
                    {
                        name: "refUserCompany.addresses",
                        select: ["*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                    },
                    {
                        name: "refUser.addresses",
                        select: ["*"],
                        page: 0,
                        size: 1,
                        where: [{ name: "refAddressType.key", type: "String", operator: "=", value: "1" }]
                    },
                    {
                        name: "products",
                        select: ["*", "refProduct.*"],
                        page: 0,
                        size: 1000000,
                        sort: [{ name: "createDate", type: "ASC" }]
                    },
                    {
                        name: "addresses",
                        select: ["*", "refAddressType.*", "refProvince.*", "refAmphur.*", "refTambon.*"],
                        page: 0,
                        size: 2,
                        where: [{ name: "refId", type: "UUID", operator: "=", value: id }]
                    },
                    {
                        name: "attachments",
                        select: ["*"],
                        page: 0,
                        size: 1000000,
                        sort: [{ name: "createDate", type: "ASC" }]
                    }
                ]
            }
        })
        .then(page => page.data[0]);
};

export const createColumns = () => {
    const columns = [
        createColumnInput({
            title: () => i18n.global.t("model.manufactureSample.refUserCompany"),
            dataIndex: "refManufactureSampleTemp.refUserCompany.companyNameTh",
            dataIndexValue: ({ record }) =>
                record.refManufactureSampleTemp?.refUserCompany?.refPersonType?.key === "1"
                    ? record.refManufactureSampleTemp?.refUserCompany?.companyNameTh
                    : record.refManufactureSampleTemp?.refUserCompany?.fullNameTh,
            customRender: ({ record, column }) => column.dataIndexValue({ record })
        }),
        createColumnInput({ title: () => i18n.global.t("model.request.draftCode"), dataIndex: "draftCode", align: "center" }),
        createColumnInput({
            title: () => i18n.global.t("model.request.requestCode"),
            dataIndex: "requestCode", align: "center",
            customRender: ({ record }) => {
                if (record.requestCode) {
                    // return <div>{`${record.requestCode}\n {แปะวันที่ส่งคำขอ}`}</div>;
                    return <div>{record.requestCode}</div>;
                }
                return <div>{record.requestCode}</div>;
            }
        }),
        createColumnInput({ title: () => i18n.global.t("model.manufactureSample.approveCode"), dataIndex: "refManufactureSample.approveCode", align: "center" }),
        createColumnDate({ title: () => i18n.global.t("model.manufactureSample.expireDate"), dataIndex: "refManufactureSample.expireDate", align: "center" }),
        createColumnSelect({
            title: () => i18n.global.t("model.manufactureSample.refOperation"),
            dataIndex: "refManufactureSampleTemp.refOperation.value",
            inputProp: () => ({
                list: ({ searchQuery }) =>
                    callUserObjective({ searchQuery }).then(operation => operation.filter(op => ["4"].includes(op.key))),
                optLabel: "value"
            }),
            align: "center",
            search: { name: "refManufactureSampleTemp.refOperation.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.request.refRequestType"),
            dataIndex: "refRequestType.value",
            customRender: ({ record }) => <div>
                <span>{record.refRequestType?.value}</span>
                {record.isSignCa ? <CIcon class="ml-1" file="sign.svg"></CIcon> : null}
            </div>,
            inputProp: () => ({ list: callRequestType, optLabel: "value" }),
            align: "center",
            search: { name: "refRequestType.id" }
        }),
        createColumnSelect({
            title: () => i18n.global.t("model.manufactureSample.refStatus"),
            dataIndex: "refStatus.value",
            inputProp: () => ({ list: callRequestStatus, optLabel: "value" }),
            align: "center",
            search: { name: "refStatus.id" }
        })
    ];
    return columns;
};

export const createField = ({ props, data, refs, key }) => {
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    data.onUpdateRefOperation = createOnUpdateRefOperation({ props, data, refs, key });
    return putField({
        data,
        fields: [
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refRequestType`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.refRequestType"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refRequestType,
                            "onUpdate:value": value => {
                                const oldValue = model.refRequestType;
                                model.refRequestType = value;
                                if (
                                    (["2"].includes(oldValue?.key) || (!["1"].includes(oldValue?.key) && ["1"].includes(value?.key))) &&
                                    model.refManufactureSample
                                ) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", { text: i18n.global.t("model.request.refRequestType") }),
                                        autoFocusButton: "cancel",
                                        onOk: () => {
                                            cloneManufactureSample({ model: { ...model, refManufactureSample: null } });
                                            if (["1"].includes(value?.key)) {
                                                const { createForm } = require("./request.logic");
                                                cloneManufactureSample({ model: createForm({ props, data, refs }) });
                                            } else {
                                                cloneManufactureSample({ model, id: model.refManufactureSample?.id });
                                            }
                                        },
                                        onCancel: () => {
                                            model.refRequestType = oldValue;
                                        }
                                    });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callRequestType({ searchQuery }).then(items => items.filter(item => !["3"].includes(item.key))),
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refManufactureSample`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.manufactureSample.approveCode"),
                inputProp: ({ field }) => {
                    return lodash.merge(
                        {
                            value: model.refManufactureSample,
                            "onUpdate:value": value => {
                                const oldValue = model.refManufactureSample;
                                model.refManufactureSample = value;
                                if (oldValue) {
                                    Modal.confirm({
                                        title: i18n.global.t("views.request.modal.change", {
                                            text: i18n.global.t("model.manufactureSample.approveCode")
                                        }),
                                        autoFocusButton: "cancel",
                                        onOk: () => cloneManufactureSample({ model, id: value?.id }),
                                        onCancel: () => {
                                            model.refManufactureSample = oldValue;
                                        }
                                    });
                                } else {
                                    cloneManufactureSample({ model, id: value?.id });
                                }
                            },
                            disabled: viewOnly,
                            list: ({ searchQuery }) => callManufactureSample({ searchQuery, userInfo: store.state.resource.auth.info?.user, isOfficerCreate: model.isOfficerCreate }),
                            renderLabel: ({ item }) => (
                                <div>
                                    {item.approveCode} - {item.refUserCompany?.fullNameTh}
                                </div>
                            ),
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    );
                },
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldSelect({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}refManufactureSampleTemp.refOperation`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.manufactureSample.refOperation"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.refManufactureSampleTemp?.refOperation,
                            "onUpdate:value": data.onUpdateRefOperation,
                            disabled: viewOnly || !model.refManufactureSampleTemp.refUserCompany,
                            list: ({ searchQuery }) => callUserObjective({ searchQuery }).then(op =>
                                    checkUserCompanyOperation({ operations: op, userCompany: model.refManufactureSampleTemp.refUserCompany })
                            ),
                            optLabel: "value",
                            allowClear: false
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    ),
                changeLog: { getValue: ({ record, field }) => lodash.get(record, `${field.key}.value`) }
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forRequestRemark.0.remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.descToOfficer"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forRequestRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forRequestRemark[0].remark = value),
                            disabled: viewOnly || model.isOfficerCreate
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            }),
            createFieldTextArea({
                visible: ({ field }) => conditionFormChecker({ props, data, executeKey: "visibleField", params: { field } }),
                key: `${prefix}lastStatus.forReceiveRemark.0.remark`,
                colProp: () => ({ xs: 24 }),
                formItemProp: () => ({ labelCol: { xs: 24, md: 8 }, wrapperCol: { xs: 24, md: 16 } }),
                label: () => i18n.global.t("model.request.descToUserCompany"),
                inputProp: ({ field }) =>
                    lodash.merge(
                        {
                            value: model.lastStatus.forReceiveRemark?.[0]?.remark,
                            "onUpdate:value": value => (model.lastStatus.forReceiveRemark[0].remark = value),
                            disabled: viewOnly || model.isOfficerCreate
                        },
                        conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field } })
                    )
            })
        ]
    });
};

export const cloneManufactureSample = ({ model, id }) => {
    if (id) {
        callManufactureSampleFull({ id }).then(manufactureSample => {
            const refManufactureSampleTemp = copyForm({ form: { ...manufactureSample, isTemp: true } });
            if (id) {
                refManufactureSampleTemp.attachments.forEach((d, index) => (d.refRealId = manufactureSample.attachments[index].id));
                refManufactureSampleTemp.products.forEach((d, index) => (d.refReal = { id: manufactureSample.products[index].id }));
            }
            return callUser({ userCompanyId: refManufactureSampleTemp.refUserCompany.id }).then(users => {
                refManufactureSampleTemp.refUser = users.find(d => d.id === store.state.resource.auth.info?.user?.id) || refManufactureSampleTemp.refUser;
                router.push({
                    name: router.currentRoute.value.name,
                    params: { props: JSON.stringify({ form: { ...model, refDepartment: manufactureSample.refDepartment, refManufactureSampleTemp } }) },
                    query: { _timestamp: new Date().getTime() }
                });
            });
        });
    } else {
        router.push({
            name: router.currentRoute.value.name,
            params: { props: JSON.stringify({ forceDefault: true, form: model }) },
            query: { _timestamp: new Date().getTime() }
        });
    }
};

export const createOnUpdateRefOperation = ({ data, key }) => {
    const { useForm } = data;
    const { modelRef } = useForm;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    return value => {
        model.refManufactureSampleTemp.refOperation = value;
        if (value) {
            callRequestDoc({}).then(items => {
                model.refRequestDoc = items.find(item => {
                    if (value.key === "4" && item.key === "03") {
                        return item;
                    }
                });
            });
        } else {
            model.refRequestDoc = null;
        }
    }
};